import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Avatar,
  Typography,
  Button,
  useMediaQuery,
  InputBase,
  CircularProgress,
  Box,
  DialogTitle,
  TextField,
  DialogActions,
  Menu,
  MenuItem,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {
  DollarSign,
  Bookmark,
  Share,
  Send,
  ChevronDown,
  ChevronUp,
  X,
  Flag,
} from "lucide-react";
import { useProfileStyles } from "../Header/ProfileViewer/profile-viewer.styles";
import { Post } from "../Header/ProfileViewer/profile-viewer";
import likeAnimStyle from "../../styles/likeAnimation";
import HeartIcon from "../../assets/HeartIcon";
import ChatBubbleIcon from "../../assets/ChatBubbleIcon";
import TipPostIcon from "../../assets/TipPostIcon";
import {
  addBookmarkAPI,
  deletePostAPI,
  followUserAPI,
  getFileByIdAPI,
  getUserGalleryPostAPI,
  getUserGalleryPostCommentsAPI,
  getUserProfile,
  likeGalleryPostAPI,
  likeUserPostCommentAPI,
  sendCommentToPostAPI,
  togglePinPostAPI,
  updateGalleryCommentAPI,
  deleteGalleryCommentAPI,
} from "../../api/userApi";
import BaseLoading from "../Loader/BaseLoading";
import UniversalSlider, { SliderItem } from "./UniversalSlider";
import { showToast } from "../../utils/toast";
import { Favorite, SentimentSatisfiedAlt, Edit } from "@material-ui/icons";
import MoreVerticalIcon from "../../assets/MoreVerticalIcon";
import CommentText from "./comment-text";
import { useHistory } from "react-router-dom";
import CommentsModal, { Comment } from "./CommentsModal";
import { useAppSelector } from "../../redux/typedHooks";
import type { UserInterface } from "../../types/user";
import { useTheme } from "@material-ui/core/styles";
import videoIconSvg from "../../assets/videoIcon.svg";
import UnlockPaidContentModal from "./UnlockPaidContentModal";
import TipDialog from "./TipDialog";
import VerifiedShieldIcon from "../../assets/VerifiedSupIcon";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import {
  formatPostTime,
  formatFullTime,
  formatPostDate,
} from "../../utils/timeFormatter";
import { useKeyboardScroll } from "../../hooks/useKeyboardScroll";
import { useGlobalMonetization } from "../../hooks/useGlobalMonetization";
import {
  trackShare,
  trackView,
  trackCompletePostView,
} from "../../utils/analyticsTracking";
import { checkRateLimit } from "../../utils/rateLimit";
import arrowBackIcon from "../../assets/images/icons/arrowBackIcon.svg";

enum FollowStatus {
  NOT_FOLLOWING = "not_following",
  FOLLOWING = "following",
  PENDING = "pending",
}

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}

interface FullPost {
  _id: string;
  description: string;
  hashtags?: string[];
  categories?: Array<{
    _id: string;
    name: string;
    description: string;
  }>;
  purchasesCount: number;
  sharesCount: number;
  likesCount: number;
  viewsCount: number;
  price: number;
  isShared: boolean;
  pinnedAt?: string | null; // НОВЕ
  createdAt?: string;

  isLiked: boolean;
  content: Array<{
    _id: string;
    mediaUrl: string;
    type: string;
    isBlurred?: boolean;
  }>;
  follow: { accepted: boolean } | null;
  user: {
    _id: string;
    username: string;
    firstName: string;
    lastName: string;
    profileImg: string;
    isFollowed: boolean;
    private: boolean;
    suggestedTips?: number[];
    minimumTipAmount?: number;
    allowCommentsOnPosts?: boolean;
  };
  isBlurred: boolean;
  access: boolean;
}

interface PostDialogProps {
  open: boolean;
  post: Post;
  onClose: () => void;
  visionType?: string;
  scrollPosition?: number;
  onPinnedChange?: (postId: string, pinnedAt: string | null) => void; // NEW
  onPostDeleted?: (postId: string) => void; // NEW - callback for post deletion
  onEditPost?: (post: Post) => void; // NEW - callback for post editing
  initialShowComments?: boolean; // NEW - для автоматичного відкриття коментарів
}

export const PostDialog: React.FC<PostDialogProps> = ({
  open,
  post,
  onClose,
  visionType,
  scrollPosition,
  onPinnedChange,
  onPostDeleted,
  onEditPost,
  initialShowComments = false,
}) => {
  const classes = useProfileStyles({ variant: "viewer" });
  const likeAnimation = likeAnimStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const me = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const { hasPremiumSubscription } = useGlobalMonetization();
  const [tipDialogVisible, setTipDialogVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const currentUserId = (me as any)?.id || (me as any)?._id;

  // Keyboard scroll management for mobile
  const { focusAndCenterInput, scrollToInput } = useKeyboardScroll({
    enabled: isMobile,
  });

  const [fullPost, setFullPost] = useState<FullPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsPage, setCommentsPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showBigLikeIndex, setShowBigLikeIndex] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCommentLikeAnimationIndex, setShowCommentLikeAnimationIndex] =
    useState<string | null>(null);
  const [text, setText] = useState("");
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [, setReplyToUsername] = useState<string | null>(null);
  const [showReplies, setShowReplies] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editText, setEditText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [pinSaving] = useState(false);
  const [, setPinLoading] = useState(false);
  const [viewStartTime, setViewStartTime] = useState<number>(0);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );

  // Wrapper function to track complete view before closing
  const handleClose = () => {
    if (viewStartTime > 0) {
      const viewDuration = (Date.now() - viewStartTime) / 1000; // Convert to seconds
      trackCompletePostView(
        post._id,
        post.type === "photo"
          ? "photo"
          : post.type === "video"
            ? "video"
            : "photo",
        viewDuration
      );
    }
    onClose();
  };

  /** Slider X: close comments panel first; only then close the whole dialog (avoids jumping to feed). */
  const handleSliderClose = () => {
    if (showComments) {
      setShowComments(false);
    } else {
      handleClose();
    }
  };

  // опціонально: повідомити батьківський список про зміну
  //   type onPinnedChange = (postId: string, pinnedAt: string | null) => void;

  const [followStatus, setFollowStatus] = useState<FollowStatus>(
    FollowStatus.NOT_FOLLOWING
  );
  const fetchedUrlsRef = useRef<Set<string>>(new Set());
  const [purchasePostDialogVisible, setPurchasePostDialogVisible] =
    useState(false);

  useEffect(() => {
    if (!open) setPurchasePostDialogVisible(false);
  }, [open]);

  const fetchMediaUrl = async (mediaUrl: string, type: string = "image") => {
    if (!mediaUrl || typeof mediaUrl !== "string") {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      return videoIconSvg;
    }
    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      return videoIconSvg;
    }

    setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching ${type} for ${mediaUrl}:`, err);
      showToast(`Failed to load ${type}`, "error");
      return videoIconSvg;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
    }
  };

  const RenderAvatar = ({
    profileImg,
    className,
    style,
  }: {
    profileImg: string;
    className: string;
    style?: React.CSSProperties;
  }) => {
    if (!profileImg) {
      return <Avatar src={videoIconSvg} className={className} style={style} />;
    }
    if (loadingMedia[profileImg]) {
      return (
        <div
          className={className}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...style,
          }}
        >
          <CircularProgress size={24} />
        </div>
      );
    }
    return (
      <Avatar
        src={mediaUrls[profileImg] || videoIconSvg}
        className={className}
        style={style}
      />
    );
  };

  useEffect(() => {
    if (!fullPost) return;

    if (
      fullPost.user.profileImg &&
      !fetchedUrlsRef.current.has(fullPost.user.profileImg)
    ) {
      fetchedUrlsRef.current.add(fullPost.user.profileImg);
      fetchMediaUrl(fullPost.user.profileImg, "avatar");
    }

    if (me?.profileImg && !fetchedUrlsRef.current.has(me.profileImg)) {
      fetchedUrlsRef.current.add(me.profileImg);
      fetchMediaUrl(me.profileImg, "avatar");
    }

    fullPost.content.forEach(contentItem => {
      if (!fetchedUrlsRef.current.has(contentItem.mediaUrl)) {
        fetchedUrlsRef.current.add(contentItem.mediaUrl);
        fetchMediaUrl(contentItem.mediaUrl, contentItem.type);
      }
    });

    comments.forEach(comment => {
      if (
        comment.user.profileImg &&
        !fetchedUrlsRef.current.has(comment.user.profileImg)
      ) {
        fetchedUrlsRef.current.add(comment.user.profileImg);
        fetchMediaUrl(comment.user.profileImg, "avatar");
      }
      comment.replies.forEach(reply => {
        if (
          reply.user.profileImg &&
          !fetchedUrlsRef.current.has(reply.user.profileImg)
        ) {
          fetchedUrlsRef.current.add(reply.user.profileImg);
          fetchMediaUrl(reply.user.profileImg, "avatar");
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPost, comments, me?.profileImg]);

  // Cleanup media URLs only when modal closes
  useEffect(() => {
    if (!open) {
      // Cleanup blob URLs when modal closes
      const currentMediaUrls = mediaUrls;
      Object.values(currentMediaUrls).forEach(url => {
        if (url && typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      setMediaUrls({});
      setLoadingMedia({});
      fetchedUrlsRef.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDeletePost = async () => {
    try {
      await deletePostAPI(post._id);
      // Clear any error messages by showing success toast
      showToast("Post deleted successfully", "success");

      // Call the callback to notify parent component
      if (onPostDeleted) {
        onPostDeleted(post._id);
      }

      // Close the dialog
      handleClose();
    } catch (error) {
      console.error("Error deleting post:", error);
      showToast("Error deleting post", "error");
    }
  };

  const toggleReplies = (commentId: string, repliesCount: number) => {
    setShowReplies(prevState => {
      const isOpening = !prevState[commentId];
      if (isOpening && repliesCount > 0) {
        loadRepliesForComment(post, commentId);
      }
      return { ...prevState, [commentId]: isOpening };
    });
  };

  const formatCommentTime = (createdAt: string): string => {
    const now = Date.now();
    const commentDate = new Date(createdAt).getTime();
    const diffMs = now - commentDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return "now";
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 4) return `${diffWeeks}w`;
    if (diffMonths < 12) return `${diffMonths}m`;
    return `${diffYears}y`;
  };

  const openEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setEditText(comment.text || "");
  };

  const handleSaveEditedComment = async () => {
    if (!editingComment || !fullPost) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      showToast("Comment text cannot be empty", "error");
      return;
    }
    try {
      setSavingComment(true);
      await updateGalleryCommentAPI(editingComment.id, {
        comment: trimmed,
      });
      // Optimistic update - update comment or reply
      setComments(prev =>
        prev.map(c => {
          // Check if this is the main comment
          if (c.id === editingComment.id) {
            return { ...c, text: trimmed };
          }
          // Check if this is a reply
          if (c.replies.some(reply => reply.id === editingComment.id)) {
            return {
              ...c,
              replies: c.replies.map(reply =>
                reply.id === editingComment.id
                  ? { ...reply, text: trimmed }
                  : reply
              ),
            };
          }
          return c;
        })
      );
      showToast("Comment updated", "success");
      setEditingComment(null);
    } catch (err: any) {
      console.error("Error updating comment", err);
      showToast(err.message || "Error updating comment", "error");
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (
    commentId: string,
    isReply: boolean = false,
    parentCommentId?: string
  ) => {
    if (!fullPost) return;

    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      await deleteGalleryCommentAPI(commentId);

      // Optimistic update - remove comment or reply
      setComments(prev => {
        if (isReply && parentCommentId) {
          // Remove reply from parent comment
          return prev.map(c => {
            if (c.id === parentCommentId) {
              return {
                ...c,
                replies: c.replies.filter(reply => reply.id !== commentId),
              };
            }
            return c;
          });
        } else {
          // Remove main comment
          return prev.filter(c => c.id !== commentId);
        }
      });

      showToast("Comment deleted", "success");
    } catch (err) {
      console.error("Error deleting comment", err);
      showToast("Error deleting comment", "error");
    }
  };

  const fetchComments = useCallback(async (pageNum = 1, append = false) => {
    setCommentsLoading(true);
    try {
      const res = await getUserGalleryPostCommentsAPI(
        post.userId,
        post._id,
        null,
        { page: pageNum, perPage: 20 }
      );
      const items = (res as any).items || res;
      const totalPages = (res as any).totalPages ?? 1;
      const currentPage = (res as any).currentPage ?? pageNum;

      const getComment = (comment: any): Comment[] =>
        comment.map((c: any) => ({
          id: c.id,
          text: c.comment,
          hashtags: c.hashtags || [],
          createdAt: c.createdAt,
          isLiked: c.isLiked,
          likesCount: c.likesCount,
          repliesCount: c.repliesCount || 0,
          replyToCommentId: c.replyToCommentId || null,
          user: {
            id: c.user.id,
            username: c.user.username,
            firstName: c.user.firstName,
            lastName: c.user.lastName,
            profileImg: c.user.profileImg,
          },
          replies: c.replies ? getComment(c.replies) : [],
        }));

      const newComments = getComment(items);

      if (append) {
        setComments(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const unique = newComments.filter(c => !existingIds.has(c.id));
          return [...prev, ...unique];
        });
      } else {
        setComments(newComments);
      }

      setCommentsPage(pageNum);
      setHasMoreComments(currentPage < totalPages);
    } catch (err) {
      console.error("Error fetching comments:", err);
      showToast("Error fetching comments", "error");
    } finally {
      setCommentsLoading(false);
    }
  }, [post.userId, post._id]);

  const loadMoreComments = useCallback(() => {
    fetchComments(commentsPage + 1, true);
  }, [commentsPage, fetchComments]);

  const handleCommentsChange = (newComments: Comment[]) => {
    setComments(newComments);
  };

  const likeClick = async () => {
    if (!fullPost) return;
    const wasLiked = fullPost.isLiked;
    setFullPost({
      ...fullPost,
      isLiked: !wasLiked,
      likesCount: fullPost.likesCount + (wasLiked ? -1 : 1),
    });
    try {
      await likeGalleryPostAPI(fullPost._id);
    } catch {
      setFullPost({
        ...fullPost,
        isLiked: wasLiked,
        likesCount: fullPost.likesCount,
      });
      showToast("Error toggling like", "error");
    }
  };

  const handleCommentLike = async (
    commentId: string,
    postId: string,
    idx: number
  ) => {
    try {
      await likeUserPostCommentAPI(postId, commentId);
      setComments(prev => {
        const copy = [...prev];
        const c = copy[idx];

        if (c.id === commentId) {
          c.isLiked = !c.isLiked;
          c.likesCount += c.isLiked ? 1 : -1;

          copy[idx] = {
            ...c,
            isLiked: c.isLiked,
            likesCount: c.likesCount,
          };
          return copy;
        }

        c.replies = c.replies.map(replie => {
          if (replie.id === commentId) {
            replie.isLiked = !replie.isLiked;
            replie.likesCount += replie.isLiked ? 1 : -1;
            return replie;
          }
          return replie;
        });

        copy[idx] = { ...c };
        return copy;
      });
    } catch (error) {
      showToast("Error liking comment", "error");
    }
  };
  const handlePurchasePost = () => {
    if (!me?.id) {
      showToast("You must be logged in to purchase content.", "error");
      return;
    }
    // Don't show purchase dialog if user is the post owner
    if (
      fullPost &&
      (me?.id === fullPost.user._id ||
        me?._id === fullPost.user._id ||
        me?.id === post.userId ||
        me?._id === post.userId)
    ) {
      return;
    }
    setPurchasePostDialogVisible(true);
  };
  const handleCompletePurchase = async () => {
    if (!fullPost) {
      showToast("No post selected for purchase.", "error");
      return;
    }
    try {
      setFullPost(prev =>
        prev
          ? {
            ...prev,
            access: true,
            content: prev.content.map(c => ({
              ...c,
              isBlurred: false,
            })),
          }
          : prev
      );
      showToast(
        `Successfully unlocked ${fullPost.content[0].type === "video" ? "video" : "image"
        }!`,
        "success"
      );
      setPurchasePostDialogVisible(false);
    } catch (err) {
      showToast("Error completing purchase.", "error");
      console.error("Purchase error:", err);
    }
  };

  const handlePurchaseSuccess = async () => {
    if (!fullPost) return;

    try {
      setFullPost(prev =>
        prev
          ? {
            ...prev,
            access: true,
            content: prev.content.map(c => ({
              ...c,
              isBlurred: false,
            })),
          }
          : prev
      );
      showToast(
        `Successfully unlocked ${fullPost.content[0].type === "video" ? "video" : "image"
        }!`,
        "success"
      );
      setPurchasePostDialogVisible(false);
    } catch (err) {
      showToast("Error completing purchase.", "error");
      console.error("Purchase error:", err);
    }
  };

  const handlePostShare = async () => {
    if (!fullPost) return;

    const url = `${window.location.origin}/${fullPost.user._id}?postId=${fullPost._id}`;
    const postType =
      fullPost.content[0]?.type === "photo"
        ? "photo"
        : fullPost.content[0]?.type === "video"
          ? "video"
          : "post";
    const shareTitle = `Check out this ${postType} by ${fullPost.user.username || fullPost.user._id
      }`;
    const shareText = fullPost.description || shareTitle;

    try {
      let sharePlatform = "clipboard";

      // Check if Web Share API is supported
      if (navigator.share && navigator.canShare) {
        const shareData: ShareData = {
          title: shareTitle,
          text: shareText,
          url: url,
        };

        // Check if the data can be shared
        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            sharePlatform = "native_share";
            showToast("Shared successfully!", "success");
          } catch (shareError: any) {
            // User cancelled or error occurred
            if (shareError.name === "AbortError") {
              // User cancelled, don't show error
              return;
            }
            // Fall through to clipboard fallback
            throw shareError;
          }
        } else {
          // Can't share with full data, try with just URL
          try {
            await navigator.share({ url });
            sharePlatform = "native_share";
            showToast("Shared successfully!", "success");
          } catch (shareError: any) {
            if (shareError.name === "AbortError") {
              return;
            }
            throw shareError;
          }
        }
      } else {
        // Web Share API not supported, use clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          showToast("Link copied to clipboard", "success");
          sharePlatform = "clipboard";
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea");
          textArea.value = url;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand("copy");
            showToast("Link copied to clipboard", "success");
            sharePlatform = "clipboard";
          } catch (err) {
            showToast("Failed to copy link", "error");
            return;
          } finally {
            document.body.removeChild(textArea);
          }
        }
      }

      // Track the share
      await trackShare(fullPost._id, postType, "link", sharePlatform, {
        shareUrl: url,
        shareText: shareText,
        surface: "post_modal",
        mvp_feature_intent: "share_post_intent",
        mvp_feature_generated: "share_post_link",
        shareMetadata: {
          postType: postType,
          creatorId: fullPost.user._id,
          creatorUsername: fullPost.user.username,
        },
      });
    } catch (error: any) {
      // Only show error if it's not a user cancellation
      if (error.name !== "AbortError") {
        console.error("Error sharing post:", error);
        showToast("Error sharing post", "error");
      }
    }
  };

  const handlePostBookmark = async () => {
    if (!fullPost) return;

    try {
      await addBookmarkAPI(fullPost._id);
      showToast("Added to bookmarks", "success");
    } catch (error) {
      console.error("Error bookmarking post:", error);
      showToast("Error bookmarking post", "error");
    }
  };

  const handleSendComment = async () => {
    if (!fullPost || !text.trim()) return;
    const key = `comment-${me?._id || me?.id || "guest"}`;
    const { allowed, retryIn } = checkRateLimit(key, 3, 60 * 1000);
    if (!allowed) {
      showToast(
        `Too many comments. Try again in ${Math.ceil(retryIn / 1000)}s`,
        "info"
      );
      return;
    }
    try {
      await sendCommentToPostAPI(text, {
        galleryPostId: fullPost._id,
        commentId: replyToId,
      });
      showToast("Comment sent");
      setText("");

      // Center input above keyboard on mobile after sending comment
      if (isMobile && inputRef.current) {
        setTimeout(() => {
          focusAndCenterInput(inputRef);
        }, 200);
      } else {
        inputRef.current?.focus();
      }

      await fetchComments();
    } catch (error: any) {
      showToast((error.message || "Error sending comment"), "error");
    }
  };

  const handleFollow = async (userId: string) => {
    try {
      const response = await followUserAPI(userId);
      const follow = response.data || null;
      const newFollowStatus = follow
        ? follow.accepted
          ? FollowStatus.FOLLOWING
          : FollowStatus.PENDING
        : FollowStatus.NOT_FOLLOWING;

      setFullPost(prev =>
        prev
          ? {
            ...prev,
            follow,
            user: {
              ...prev.user,
              isFollowed: follow ? follow.accepted : false,
            },
          }
          : prev
      );
      setFollowStatus(newFollowStatus);

      if (!follow) {
        showToast("Unfollowed successfully", "success");
      } else if (!follow.accepted) {
        showToast("Follow request sent", "info");
      } else {
        showToast("Followed successfully", "success");
      }
    } catch (err) {
      showToast(
        (err as Error)?.message || "Error while following creator!",
        "error"
      );
    }
  };

  const handleTipFromMenu = () => {
    if (!fullPost) return;
    setMenuPos(null);
    setTipDialogVisible(true);
  };

  const handleFollowFromMenu = async () => {
    if (!fullPost) return;
    const userId = fullPost.user._id;
    // Don't close menu immediately - let user see the change
    await handleFollow(userId);
    // Close menu after update with delay to show the change
    setTimeout(() => {
      setMenuPos(null);
    }, 500);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setShowComments(initialShowComments || false);

    // Track view when modal opens
    const startTime = Date.now();
    setViewStartTime(startTime);
    trackView(
      post._id,
      post.type === "photo"
        ? "photo"
        : post.type === "video"
          ? "video"
          : "post",
      {
        isCompleteView: false,
        viewDuration: 0,
      }
    );

    (async () => {
      try {
        const data = await getUserGalleryPostAPI(post.userId, post._id);
        const pd: any = (data as any).post || data;
        const profileData = await getUserProfile(post.userId);
        setFullPost({
          _id: pd._id,
          description: pd.description,
          hashtags: pd.hashtags || [],
          categories: pd.categories || [],
          purchasesCount: pd.purchasesCount,
          sharesCount: pd.sharesCount,
          likesCount: pd.likesCount,
          viewsCount: pd.viewsCount,
          price: pd.price || 0,
          isShared: pd.isShared ?? false,
          isLiked: pd.isLiked ?? false,
          content: pd.content.map((c: any) => ({
            ...c,
            mediaUrl: c.mediaUrl,
            isBlurred: me?.id === post.userId ? false : c.isBlurred,
          })),
          follow: pd.follow,
          user: {
            ...pd.user,
            private: pd.user.private ?? false,
            suggestedTips: profileData.suggestedTips || [5, 10, 15, 20, 25],
            minimumTipAmount: profileData.minimumTipAmount || 2,
            allowCommentsOnPosts:
              profileData?.privacySettings?.allowCommentsOnPosts !== false,
          },
          isBlurred: me?.id === post.userId ? false : (pd.isBlurred ?? false),
          access: pd.access ?? true,
          pinnedAt: pd.pinnedAt ?? null, // НОВЕ
          createdAt: pd.createdAt || post.createdAt,
        });
        setFollowStatus(
          pd.follow
            ? pd.follow.accepted
              ? FollowStatus.FOLLOWING
              : FollowStatus.PENDING
            : FollowStatus.NOT_FOLLOWING
        );
        await fetchComments();
      } catch (err) {
        console.error("Error loading post:", err);
        showToast("Error loading post", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [
    open,
    post.userId,
    post._id,
    post.createdAt,
    fetchComments,
    me?.id,
    post.type,
    initialShowComments,
  ]);

  useEffect(() => {
    if (open && typeof scrollPosition === "number") {
      const alreadySaved = sessionStorage.getItem("myFeedScrollPosition");
      if (!alreadySaved) {
        sessionStorage.setItem(
          "myFeedScrollPosition",
          scrollPosition.toString()
        );
      }
    }
  }, [open, scrollPosition]);

  // const handleClose = () => {
  //   onClose();
  //   history.replace({
  //     pathname: history.location.pathname,
  //     search: visionType ? `?visionType=${visionType}` : "",
  //     state: {
  //       visionType,
  //       scrollPosition: scrollPosition || Number(sessionStorage.getItem("myFeedScrollPosition")) || 0,
  //     },
  //   });
  // };

  const handleProfileClick = (userId: string) => {
    const currentScroll = window.scrollY;
    sessionStorage.setItem("myFeedScrollPosition", currentScroll.toString());

    history.push({
      pathname: `/${userId}`,
      state: {
        fromPostDialog: true,
        postId: post._id,
        userId: post.userId,
        previousPath: history.location.pathname + history.location.search,
        visionType,
        scrollPosition: currentScroll,
      },
    });

    handleClose();
  };

  const handleEmojiSelect = (emoji: any) => {
    setText(prev => prev + (emoji?.native || emoji?.unified || ""));
    setShowEmojiPicker(false);
  };

  const handleTogglePinHere = async () => {
    if (!fullPost) return;
    setPinLoading(true);

    const willPin = !fullPost.pinnedAt;
    const prevPinnedAt = fullPost.pinnedAt ?? null;
    const optimisticPinnedAt = willPin ? new Date().toISOString() : null;

    // 1) Оптимістично оновлюємо діалог
    setFullPost(fp => (fp ? { ...fp, pinnedAt: optimisticPinnedAt } : fp));
    // 2) Повідомляємо ProfilePage
    onPinnedChange?.(fullPost._id, optimisticPinnedAt);

    try {
      const res = await togglePinPostAPI(fullPost._id, willPin);
      // не покладайся на res.success — візьми pinnedAt, якщо є, інакше залиш оптимістичне
      const serverPinnedAt =
        res && res.pinnedAt !== undefined ? res.pinnedAt : optimisticPinnedAt;

      setFullPost(fp => (fp ? { ...fp, pinnedAt: serverPinnedAt } : fp));
      onPinnedChange?.(fullPost._id, serverPinnedAt);

      showToast(serverPinnedAt ? "Post pinned" : "Post unpinned", "success");
    } catch (err) {
      // Ролбек у діалозі та лістингу
      setFullPost(fp => (fp ? { ...fp, pinnedAt: prevPinnedAt } : fp));
      onPinnedChange?.(fullPost._id, prevPinnedAt);
      showToast((err as Error)?.message || "Failed to toggle pin", "error");
    } finally {
      setPinLoading(false);
    }
  };

  const loadRepliesForComment = async (post: Post, commentId: string) => {
    const countAllReplies = (replies: any[]): number =>
      replies.reduce((acc, reply) => acc + 1 + countAllReplies(reply.replies || []), 0);

    try {
      const res = await getUserGalleryPostCommentsAPI(
        post.userId,
        post._id,
        commentId as any,
        { page: 1, perPage: 50 }
      );
      const items = (res as any).items || res;

      const repliesWithNested = await Promise.all(
        items.map(async (c: any) => {
          let nestedReplies: any[] = [];
          if (c.repliesCount > 0) {
            try {
              const nestedRes = await getUserGalleryPostCommentsAPI(
                post.userId,
                post._id,
                c.id,
                { page: 1, perPage: 50 }
              );
              nestedReplies = (nestedRes as any).items || [];
            } catch (e) {
              console.error('Error loading nested replies', e);
            }
          }
          return {
            id: c.id,
            text: c.comment,
            hashtags: c.hashtags || [],
            createdAt: c.createdAt,
            isLiked: c.isLiked,
            likesCount: c.likesCount,
            repliesCount: c.repliesCount || 0,
            replyToCommentId: c.replyToCommentId,
            user: {
              id: c.user.id,
              username: c.user.username,
              firstName: c.user.firstName,
              lastName: c.user.lastName,
              profileImg: c.user.profileImg,
            },
            replies: nestedReplies.map((r: any) => ({
              id: r.id,
              text: r.comment,
              hashtags: r.hashtags || [],
              createdAt: r.createdAt,
              isLiked: r.isLiked,
              likesCount: r.likesCount,
              repliesCount: r.repliesCount || 0,
              replyToCommentId: r.replyToCommentId,
              user: {
                id: r.user.id,
                username: r.user.username,
                firstName: r.user.firstName,
                lastName: r.user.lastName,
                profileImg: r.user.profileImg,
              },
              replies: [],
            })),
          };
        })
      );

      const repliesCountAllWithNested = countAllReplies(repliesWithNested);

      setComments(prev => prev.map((c: any) =>
        c.id === commentId
          ? { ...c, replies: repliesWithNested, repliesCountAllWithNested }
          : c
      ));
    } catch (err) {
      console.error("Error loading replies:", err);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      fullScreen={isMobile}
      BackdropProps={
        isMobile
          ? {
            style: {
              backgroundColor: "transparent",
            },
          }
          : undefined
      }
      PaperProps={{
        className: classes.dialogPaper,
        style: !isMobile
          ? {
            minWidth: showComments ? 1080 : 656,
            width: showComments ? "min(92vw, 1180px)" : 656,
            maxWidth: showComments ? 1180 : 656,
            backgroundColor: "#181615",
            border: "1px solid #FFFFFF1A",
            borderRadius: 16,
            padding: 24,
            maxHeight: "90vh",
            overflow: "hidden",
          }
          : {
            // Mobile: keep global header visible and avoid extra dimmer area
            backgroundColor: "#0C0A09",
            marginTop: 64, // header height on mobile
            height: "calc(100vh - 64px)",
            maxHeight: "calc(100vh - 64px)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            overflow: "hidden",
          },
      }}
    >
      {isMobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "12px 8px",
            backgroundColor: "#0C0A09",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={handleClose}
            style={{
              color: "white",
              padding: "8px",
            }}
          >
            <img
              src={arrowBackIcon}
              alt="Back"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </div>
      )}
      <DialogContent
        className={classes.dialogContent}
        style={
          isMobile
            ? {
              padding: 0,
              height: "calc(100vh - 56px)",
              backgroundColor: "#0C0A09",
            }
            : {
              padding: 0,
              height: "auto",
              maxHeight: "90vh",
              overflow: "hidden",
            }
        }
      >
        {loading || !fullPost ? (
          <div className={classes.loadingContainer}>
            <BaseLoading />
          </div>
        ) : (
          <>
            <div
              className={likeAnimation.likeOverlay}
              style={{ marginLeft: isMobile ? "0" : "-200px" }}
            >
              {showBigLikeIndex === post._id && (
                <Favorite className={likeAnimation.bigHeart} />
              )}
            </div>

            {isMobile ? (
              <>
                <div
                  className={classes.creatorHeader}
                  style={{
                    padding: "8px 8px 0px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    className={classes.creatorName}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      textDecoration: "none",
                      flex: 1,
                    }}
                    onClick={() => {
                      history.push({
                        pathname: `/${fullPost.user.username}`,
                        state: {
                          fromPostDialog: true,
                          previousPath:
                            history.location.pathname + history.location.search,
                        },
                      });
                      onClose();
                    }}
                  >
                    <RenderAvatar
                      profileImg={fullPost.user.profileImg}
                      className={classes.smallAvatar}
                      style={{
                        width: 40,
                        height: 40,
                        marginRight: "8px",
                      }}
                    />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        component="div"
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#FFFFFF",
                          lineHeight: 1.2,
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        {fullPost.user.firstName} {fullPost.user.lastName}
                        <VerifiedShieldIcon className={classes.verifiedIcon} />
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#ED9CB7",
                          lineHeight: 1.2,
                          marginTop: "2px",
                        }}
                      >
                        @{fullPost.user.username}
                      </Typography>
                    </div>
                  </div>
                  {isMobile && (
                    <>
                      <IconButton
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          const rect = (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect();
                          setMenuPos({ top: rect.bottom, left: rect.right });
                        }}
                        style={{ color: "#fff" }}
                      >
                        <MoreVerticalIcon />
                      </IconButton>
                      <Menu
                        open={Boolean(menuPos)}
                        onClose={() => setMenuPos(null)}
                        anchorReference="anchorPosition"
                        anchorPosition={menuPos || { top: 0, left: 0 }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        PaperProps={{ style: { position: "fixed" } }}
                        disablePortal={false}
                        className={classes.menu}
                      >
                        <MenuItem
                          className={classes.menuItemTip}
                          onClick={handleTipFromMenu}
                          style={{
                            backgroundColor: "#FFFFFF08",
                            padding: "11.25px 17.25px",
                            color: "#ffffff",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            borderRadius: "6px",
                          }}
                        >
                          <TipPostIcon />
                          Tip Creator
                        </MenuItem>
                        <MenuItem
                          disabled
                          className={classes.menuDivider}
                          style={{
                            borderTop: "1px solid #FFFFFF08",
                            margin: "12px 0",
                            padding: 0,
                            height: "1px",
                            minHeight: "1px",
                          }}
                        />
                        <MenuItem
                          onClick={handleFollowFromMenu}
                          className={classes.menuItemFollow}
                          style={{
                            backgroundColor: "#FFFFFF08",
                            color: "#ffffff",
                            fontSize: "14px",
                            padding: "11.25px 17.25px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            borderRadius: "6px",
                          }}
                        >
                          {(() => {
                            if (!fullPost) return null;
                            const isCurrentlyFollowing =
                              followStatus === FollowStatus.FOLLOWING ||
                              (followStatus === undefined &&
                                fullPost.user.isFollowed);

                            return isCurrentlyFollowing ? (
                              <>
                                <X size={24} />
                                Unfollow
                              </>
                            ) : (
                              <>
                                <X size={24} />
                                Follow
                              </>
                            );
                          })()}
                        </MenuItem>
                        <MenuItem
                          className={classes.menuItemReport}
                          onClick={() => {
                            setMenuPos(null);
                            if (me?.id === fullPost.user._id) {
                              handleDeletePost();
                            } else {
                              // TODO: Implement report post functionality
                              showToast(
                                "Report functionality coming soon",
                                "info"
                              );
                            }
                          }}
                          style={{
                            backgroundColor: "#FFFFFF08",
                            color: "#FF4574",
                            fontSize: "14px",
                            padding: "11.25px 17.25px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "6px",
                            borderRadius: "6px",
                          }}
                        >
                          <Flag size={24} />
                          {me?.id === fullPost.user._id
                            ? "Delete Post"
                            : "Report Post"}
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </div>
                <div
                  style={{
                    flex: showComments ? "0 0 40%" : "0 0 auto",
                    margin: 0,
                    padding: "4px 0",
                    maxHeight: "max-content",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#0C0A09",
                    height: "100%",
                    width: "auto",
                  }}
                >
                  <UniversalSlider
                    items={fullPost.content.map<SliderItem>(c => ({
                      id: c._id,
                      originalMediaUrl: c.mediaUrl,
                      mediaUrl: mediaUrls[c.mediaUrl] || videoIconSvg,
                      type: c.type === "video" ? "video" : "image",
                      isBlurred: c.isBlurred,
                    }))}
                    shouldPlayVideo={post.access && !fullPost.isBlurred}
                    height="auto"
                    settings={{
                      infinite: fullPost.content.length > 1,
                      speed: 500,
                      adaptiveHeight: true,
                    }}
                    loadingMedia={loadingMedia}
                    price={fullPost.price}
                    onDoubleClick={() => {
                      setShowBigLikeIndex(post._id);
                      setIsAnimating(false);
                      likeClick();
                      setTimeout(() => setIsAnimating(true), 0);
                      setTimeout(() => setIsAnimating(false), 500);
                      setTimeout(() => {
                        setShowBigLikeIndex("");
                      }, 1000);
                    }}
                    onPurchaseClick={handlePurchasePost}
                    onClose={handleSliderClose}
                    onLike={likeClick}
                    onComment={() => {
                      setShowComments(true);
                    }}
                    onBookmark={() => { }}
                    onTip={() => { }}
                    onShare={handlePostShare}
                    postOwnerId={fullPost.user._id}
                    currentUserId={me?.id || me?._id}
                    hasPremiumSubscription={hasPremiumSubscription}
                  />
                </div>
                {/* Description section for mobile */}
                <div
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#0C0A09",
                  }}
                >
                  {/* Username */}
                  <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#FFFFFF",
                      marginBottom: "8px",
                    }}
                  >
                    @{fullPost.user.username}
                  </Typography>

                  {/* Description */}
                  {fullPost.description && (
                    <Typography
                      style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#FFFFFF",
                        marginBottom: "8px",
                        lineHeight: 1.5,
                      }}
                    >
                      {fullPost.description}
                    </Typography>
                  )}

                  {/* Hashtags */}
                  {fullPost.hashtags && fullPost.hashtags.length > 0 && (
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ marginBottom: "8px", gap: "8px" }}
                    >
                      {fullPost.hashtags.map((tag, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FFFFFF",
                          }}
                        >
                          #{tag}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Categories */}
                  {fullPost.categories && fullPost.categories.length > 0 && (
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ marginBottom: "8px", gap: "8px" }}
                    >
                      {fullPost.categories.map(category => (
                        <Box
                          key={category._id}
                          style={{
                            backgroundColor: "#363636",
                            color: "#fff",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {category.name}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Date */}
                  <Typography
                    variant="body2"
                    style={{
                      color: "#A3A3A3",
                      fontSize: "14px",
                      fontWeight: 400,
                      marginBottom: "12px",
                    }}
                  >
                    {formatPostDate(fullPost.createdAt || post.createdAt)}
                  </Typography>

                  {/* Actions: Like, Comment, Share, Bookmark */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingTop: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setIsAnimating(false);
                          likeClick();
                          setTimeout(() => setIsAnimating(true), 0);
                          setTimeout(() => setIsAnimating(false), 500);
                        }}
                        style={{
                          padding: "4px",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <HeartIcon
                          filled={fullPost.isLiked}
                          size={24}
                          className={
                            isAnimating ? likeAnimation.favoritePop : ""
                          }
                          style={{
                            transition: "fill 0.3s, stroke 0.3s",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        />
                        <Typography
                          variant="body2"
                          component="span"
                          style={{ marginLeft: 4, fontSize: "14px" }}
                        >
                          {fullPost.likesCount}
                        </Typography>
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setShowComments(prev => !prev);
                          if (!showComments && isMobile && inputRef.current) {
                            setTimeout(() => {
                              scrollToInput(inputRef);
                            }, 200);
                          }
                        }}
                        style={{
                          padding: "4px",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <ChatBubbleIcon size={24} />
                        <Typography
                          variant="body2"
                          component="span"
                          style={{ marginLeft: 4, fontSize: "14px" }}
                        >
                          {comments.length}
                        </Typography>
                      </IconButton>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <IconButton
                        onClick={handlePostShare}
                        style={{ padding: "8px" }}
                      >
                        <Share color="white" size={20} />
                      </IconButton>
                      <IconButton
                        onClick={handlePostBookmark}
                        style={{ padding: "8px" }}
                      >
                        <Bookmark
                          color="white"
                          size={20}
                          style={{
                            transition: "color 0.3s",
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    </div>
                  </div>
                </div>

                {/* Comments Modal */}
                {showComments && fullPost && (
                  <CommentsModal
                    open={showComments}
                    postId={post._id}
                    postUserId={post.userId}
                    currentUserId={currentUserId}
                    postOwnerId={fullPost.user._id}
                    comments={comments}
                    onCommentsChange={handleCommentsChange}
                    onClose={() => setShowComments(false)}
                    onFetchComments={fetchComments}
                    mediaUrls={mediaUrls}
                    loadingMedia={loadingMedia}
                    onLoadReplies={(commentId) => loadRepliesForComment(post, commentId)}
                    hasMoreByPost={hasMoreComments}
                    handleLoadMoreComments={loadMoreComments}
                    RenderAvatar={RenderAvatar}
                    isMobile={isMobile}
                    classes={classes}
                    variant="mobile"
                    currentUserProfileImg={me?.profileImg}
                    postOwnerUsername={fullPost.user.username}
                    postOwnerFullName={`${fullPost.user.firstName} ${fullPost.user.lastName}`}
                    postOwnerProfileImg={fullPost.user.profileImg}
                    commentsAllowed={fullPost.user.allowCommentsOnPosts !== false}
                    autoScrollOnOpen={initialShowComments}
                    postOptionsMenu={{
                      onInsights: () => {
                        showToast("Insights coming soon", "info");
                      },
                      onEdit:
                        currentUserId === fullPost.user._id && onEditPost
                          ? () => {
                            const postForEdit: Post = {
                              ...post,
                              description: fullPost.description,
                              hashtags: fullPost.hashtags,
                              price: fullPost.price,
                              pinnedAt: fullPost.pinnedAt ?? post.pinnedAt,
                              createdAt: fullPost.createdAt ?? post.createdAt,
                            };
                            onEditPost(postForEdit);
                          }
                          : undefined,
                      onDelete:
                        currentUserId === fullPost.user._id
                          ? handleDeletePost
                          : undefined,
                    }}
                  />
                )}
              </>
            ) : (
              <Box
                style={{
                  display: "flex",
                  flexDirection: showComments ? "row" : "column",
                  width: "100%",
                  maxHeight: "90vh",
                  overflow: "hidden",
                  gap: showComments ? "32px" : "0",
                }}
              >
                <Box
                  style={{
                    width: showComments ? 656 : "100%",
                    flexShrink: 0,
                    maxHeight: "90vh",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      width: "100%",
                      borderRadius: 16,
                      overflow: "hidden",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <UniversalSlider
                      items={fullPost.content.map<SliderItem>(c => {
                        // Для відео бекенд вже надає правильний URL (заблюрене прев'ю або реальне відео) через mediaUrl
                        // Використовуємо mediaUrls якщо є, інакше використовуємо c.mediaUrl напряму
                        const mediaUrl =
                          mediaUrls[c.mediaUrl] ||
                          (loadingMedia[c.mediaUrl] ? undefined : c.mediaUrl) ||
                          (c.type === "video" ? undefined : videoIconSvg);
                        return {
                          id: c._id,
                          originalMediaUrl: c.mediaUrl,
                          mediaUrl: mediaUrl || c.mediaUrl || videoIconSvg,
                          type: c.type === "video" ? "video" : "image",
                          isBlurred: c.isBlurred,
                        };
                      })}
                      shouldPlayVideo={post.access && !fullPost.isBlurred}
                      height={"auto"}
                      settings={{
                        infinite: fullPost.content.length > 1,
                        speed: 500,
                      }}
                      loadingMedia={loadingMedia}
                      price={fullPost.price}
                      onDoubleClick={() => {
                        setShowBigLikeIndex(post._id);
                        setIsAnimating(false);
                        likeClick();
                        setTimeout(() => setIsAnimating(true), 0);
                        setTimeout(() => setIsAnimating(false), 500);
                        setTimeout(() => {
                          setShowBigLikeIndex("");
                        }, 1000);
                      }}
                      onPurchaseClick={handlePurchasePost}
                      onClose={handleSliderClose}
                      onLike={likeClick}
                      onComment={() => {
                        setShowComments(prev => !prev);
                      }}
                      onBookmark={handlePostBookmark}
                      onTip={() => {
                        setTipDialogVisible(true);
                      }}
                      onShare={handlePostShare}
                      username={fullPost.user.username}
                      description={fullPost.description}
                      hashtags={fullPost.hashtags}
                      createdAt={fullPost.createdAt || post.createdAt}
                      isLiked={fullPost.isLiked}
                      showComments={showComments}
                      postOwnerId={fullPost.user._id}
                      currentUserId={me?.id || me?._id}
                      hasPremiumSubscription={hasPremiumSubscription}
                    />
                  </Box>
                </Box>

                {showComments && fullPost && (
                  <CommentsModal
                    open={showComments}
                    postId={post._id}
                    postUserId={post.userId}
                    currentUserId={currentUserId}
                    postOwnerId={fullPost.user._id}
                    comments={comments}
                    onCommentsChange={handleCommentsChange}
                    onClose={() => setShowComments(false)}
                    onFetchComments={fetchComments}
                    mediaUrls={mediaUrls}
                    loadingMedia={loadingMedia}
                    onLoadReplies={(commentId) => loadRepliesForComment(post, commentId)}
                    hasMoreByPost={hasMoreComments}
                    handleLoadMoreComments={loadMoreComments}
                    RenderAvatar={RenderAvatar}
                    isMobile={isMobile}
                    classes={classes}
                    variant="desktop"
                    currentUserProfileImg={me?.profileImg}
                    postOwnerUsername={fullPost.user.username}
                    postOwnerFullName={`${fullPost.user.firstName} ${fullPost.user.lastName}`}
                    postOwnerProfileImg={fullPost.user.profileImg}
                    commentsAllowed={fullPost.user.allowCommentsOnPosts !== false}
                    description={fullPost.description}
                    hashtags={fullPost.hashtags}
                    createdAt={fullPost.createdAt || post.createdAt}
                    autoScrollOnOpen={initialShowComments}
                    postOptionsMenu={{
                      onInsights: () => {
                        showToast("Insights coming soon", "info");
                      },
                      onEdit:
                        currentUserId === fullPost.user._id && onEditPost
                          ? () => {
                            const postForEdit: Post = {
                              ...post,
                              description: fullPost.description,
                              hashtags: fullPost.hashtags,
                              price: fullPost.price,
                              pinnedAt: fullPost.pinnedAt ?? post.pinnedAt,
                              createdAt: fullPost.createdAt ?? post.createdAt,
                            };
                            onEditPost(postForEdit);
                          }
                          : undefined,
                      onDelete:
                        currentUserId === fullPost.user._id
                          ? handleDeletePost
                          : undefined,
                    }}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <TipDialog
        open={tipDialogVisible}
        onClose={() => setTipDialogVisible(false)}
        suggestedTips={fullPost?.user.suggestedTips}
        minimumTipAmount={fullPost?.user.minimumTipAmount}
        username={fullPost?.user.username}
        tipForUserId={fullPost?.user._id}
        userId={me?.id}
        email={me?.email}
        onPaymentSuccess={(amount: number) => {
          showToast(
            `Successfully tipped $${amount} to ${fullPost?.user.username || "this creator"
            }!`,
            "success"
          );
          setTipDialogVisible(false);
        }}
        validateBeforeStep1={amount => {
          if (!fullPost?.user._id || !me?.id || amount <= 0) {
            showToast("Please enter a valid tip amount.", "error");
            return false;
          }
          return true;
        }}
      />
      {fullPost && purchasePostDialogVisible && (
        <UnlockPaidContentModal
          open={purchasePostDialogVisible}
          onClose={() => setPurchasePostDialogVisible(false)}
          previewUrl={
            mediaUrls[fullPost.content[0]?.mediaUrl] || videoIconSvg
          }
          isVideo={fullPost.content[0]?.type === "video"}
          price={fullPost.price}
          userId={me?.id || (me as any)?._id || ""}
          email={me?.email || ""}
          postId={fullPost._id}
          tipForUserId={fullPost.user._id}
          onProceed={handleCompletePurchase}
          onPaymentSuccess={handlePurchaseSuccess}
        />
      )}
      <Dialog
        open={!!editingComment}
        onClose={() => setEditingComment(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
          },
        }}
      >
        <DialogTitle style={{ color: "#fff" }}>Edit Comment</DialogTitle>
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <TextField
            label="Comment"
            multiline
            minRows={3}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            variant="outlined"
            fullWidth
            placeholder="Enter your comment..."
            className={classes.editPostTextField}
          />
        </DialogContent>
        <DialogActions
          style={{ padding: "16px 24px", borderTop: "1px solid #333" }}
        >
          <Button
            onClick={() => {
              setEditingComment(null);
            }}
            style={{ color: "#999" }}
            disabled={savingComment}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditedComment}
            style={{
              backgroundColor: "#FE5A97",
              color: "#fff",
            }}
            disabled={savingComment || !editText.trim()}
          >
            {savingComment ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PostDialog;
